/**
 * Pure helpers behind the add wizard's identification mount (ENG-4832 / ENG-4833).
 */
import type { Brand, Model } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { Collection } from '#~/api/device/registry/index.js';
import type { Template } from '#~/device/registry/index.js';

/** Display names discovery resolved for a row. */
export type DetectedCamera = { brand?: string | undefined; model?: string | undefined };

/** The discovery fields that carry a registry match (both optional on every variant). */
export type DetectionSource = { modelId?: T_Maybe<Model['id']>; brandId?: T_Maybe<Brand['id']> };

/**
 * What discovery identified for the selected row, as display names.
 *
 * - Full fingerprint match (`modelId`): brand + model names from that model's
 *   registry template (names stay undefined while it loads; callers re-run).
 * - Brand-only match (`brandId`, no `modelId`): the brand name.
 * - Anything else, or no row: `undefined` — not identified by discovery.
 */
export const resolveDetectedCamera = (
  selection: T_Maybe<DetectionSource>,
  template: T_Maybe<Template>,
  brands: T_Maybe<Collection<Brand>>
): T_Maybe<DetectedCamera> => {
  if (!selection) return undefined;

  const brandName = (id: T_Maybe<Brand['id']>): T_Maybe<string> =>
    id ? brands?.items[id]?.name : undefined;

  if (selection.modelId) {
    // A template for another model (stale while the new one loads) names nothing.
    const matched = template?.model.id === selection.modelId ? template : undefined;

    return {
      brand: brandName(matched?.model.brandId ?? selection.brandId ?? undefined),
      model: matched?.model.name
    };
  }

  if (selection.brandId) {
    return { brand: brandName(selection.brandId), model: undefined };
  }

  return undefined;
};

/**
 * The nickname to keep when the not-listed flow finishes.
 *
 * The typed "<Brand> <Model>" replaces the current name only when the user has
 * not renamed the camera — the name is empty, one the wizard derived itself
 * (`derivedNames`: discovery row name, detected model name, registry default),
 * or the label written by an earlier pass of this flow. With nothing typed now,
 * a label written by an earlier pass is dropped so the save falls back to the
 * usual default name.
 */
export const resolveTypedNickname = ({
  current,
  derivedNames,
  previousTypedLabel,
  typedLabel
}: {
  current: T_Maybe<string>;
  derivedNames: readonly T_Maybe<string>[];
  previousTypedLabel: string;
  typedLabel: string;
}): T_Maybe<string> => {
  const ours = !!current && current === previousTypedLabel;
  const derived = !!current && derivedNames.some((name) => !!name && name === current);

  // The user typed their own nickname: never overwrite it.
  if (current && !ours && !derived) return current;

  if (typedLabel.length > 0) return typedLabel;

  return ours ? undefined : current;
};

/**
 * The camera as the "uses basic controls" notice names it (D1, and each row of
 * the multi-add list). A generic registry row is called "Other", so "Sony Other
 * uses basic controls" would read as a model name; fall back to the brand
 * alone, or to "This camera" when both names are the catch-all.
 */
export const basicControlsCameraLabel = ({
  brandName,
  modelName
}: {
  brandName: string;
  modelName: string;
}): string => {
  const isOther = (name: string): boolean => name.trim() === '' || /other/i.test(name);

  if (isOther(brandName)) return 'This camera';
  if (isOther(modelName)) return `${brandName} camera`;

  return `${brandName} ${modelName}`;
};
